import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { usePulseAnimation } from "../hooks/usePulseAnimation";
import { useToast } from "../hooks/useToast";
import { lockBodyScroll, unlockBodyScroll } from "../utils/scrollLock";

export type ProgressiveTempoEditorProps = {
  isOpen: boolean;
  onCancel: () => void;
  onSave: (increment: number, frequency: number) => void;
};

export const ProgressiveTempoEditor: React.FC<ProgressiveTempoEditorProps> = ({
  isOpen,
  onCancel,
  onSave,
}) => {
  const { showInfo, showSuccess } = useToast();

  // Match SongStructureEditor animation pattern
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  // State for input fields
  const [increment, setIncrement] = useState<number>(1);
  const [frequency, setFrequency] = useState<number>(1);
  const [incrementDisplay, setIncrementDisplay] = useState<string>("1");
  const [frequencyDisplay, setFrequencyDisplay] = useState<string>("1");

  // Track initial values to determine if settings changed
  const [initialIncrement, setInitialIncrement] = useState<number>(1);
  const [initialFrequency, setInitialFrequency] = useState<number>(1);

  // Pulse animation for Apply button
  const {
    pulseState: savePulse,
    scaleState: saveScale,
    triggerPulse: triggerSavePulse,
  } = usePulseAnimation();

  // Validation ranges
  const INCREMENT_MIN = 1;
  const INCREMENT_MAX = 50;
  const FREQUENCY_MIN = 1;
  const FREQUENCY_MAX = 100;

  // Validation function for increment
  const validateAndClampIncrement = (value: number): number => {
    if (
      !Number.isFinite(value) ||
      value < INCREMENT_MIN ||
      value > INCREMENT_MAX ||
      value === 0 // Handle empty field case
    ) {
      const clamped =
        Number.isFinite(value) && value > 0
          ? Math.max(INCREMENT_MIN, Math.min(INCREMENT_MAX, value))
          : INCREMENT_MIN;
      if (value !== INCREMENT_MIN) {
        showInfo(
          `The valid range for the BPM increment is ${INCREMENT_MIN} to ${INCREMENT_MAX}.`
        );
      }
      return clamped;
    }
    return value;
  };

  // Validation function for frequency
  const validateAndClampFrequency = (value: number): number => {
    if (
      !Number.isFinite(value) ||
      value < FREQUENCY_MIN ||
      value > FREQUENCY_MAX ||
      value === 0 // Handle empty field case
    ) {
      const clamped =
        Number.isFinite(value) && value > 0
          ? Math.max(FREQUENCY_MIN, Math.min(FREQUENCY_MAX, value))
          : FREQUENCY_MIN;
      if (value !== FREQUENCY_MIN) {
        showInfo(
          `The valid range for the frequency is ${FREQUENCY_MIN} to ${FREQUENCY_MAX}.`
        );
      }
      return clamped;
    }
    return value;
  };

  // Load saved values from localStorage
  useEffect(() => {
    if (isOpen) {
      try {
        const savedIncrement = localStorage.getItem(
          "progressiveTempo.increment"
        );
        const savedFrequency = localStorage.getItem(
          "progressiveTempo.frequency"
        );

        let loadedIncrement = 1;
        let loadedFrequency = 1;

        if (savedIncrement) {
          const parsed = parseInt(savedIncrement, 10);
          if (!isNaN(parsed)) {
            loadedIncrement = parsed;
            setIncrement(parsed);
            setIncrementDisplay(parsed.toString());
          }
        }

        if (savedFrequency) {
          const parsed = parseInt(savedFrequency, 10);
          if (!isNaN(parsed)) {
            loadedFrequency = parsed;
            setFrequency(parsed);
            setFrequencyDisplay(parsed.toString());
          }
        }

        // Set initial values for comparison
        setInitialIncrement(loadedIncrement);
        setInitialFrequency(loadedFrequency);
      } catch {}
    }
  }, [isOpen]);

  // Validation is defined at component level
  const isValid =
    (incrementDisplay === "" ||
      (Number.isFinite(increment) &&
        increment >= INCREMENT_MIN &&
        increment <= INCREMENT_MAX)) &&
    (frequencyDisplay === "" ||
      (Number.isFinite(frequency) &&
        frequency >= FREQUENCY_MIN &&
        frequency <= FREQUENCY_MAX));

  // Handler for increment blur/enter
  const handleIncrementValidation = () => {
    // If display is empty, treat as minimum value
    const valueToValidate = incrementDisplay === "" ? 0 : increment;
    const validated = validateAndClampIncrement(valueToValidate);
    setIncrement(validated);
    setIncrementDisplay(validated.toString());
  };

  // Handler for frequency blur/enter
  const handleFrequencyValidation = () => {
    // If display is empty, treat as minimum value
    const valueToValidate = frequencyDisplay === "" ? 0 : frequency;
    const validated = validateAndClampFrequency(valueToValidate);
    setFrequency(validated);
    setFrequencyDisplay(validated.toString());
  };

  // Apply settings function
  const applySettings = () => {
    // Check if settings have changed
    const hasChanged =
      increment !== initialIncrement || frequency !== initialFrequency;

    if (hasChanged && isValid) {
      // Persist to localStorage
      try {
        localStorage.setItem("progressiveTempo.increment", String(increment));
        localStorage.setItem("progressiveTempo.frequency", String(frequency));
      } catch {}

      onSave(increment, frequency);
      showSuccess("Progressive Tempo settings updated");

      // Update initial values
      setInitialIncrement(increment);
      setInitialFrequency(frequency);
    }
  };

  const handleApply = (event: React.MouseEvent<HTMLButtonElement>) => {
    triggerSavePulse(event);
    applySettings();
    onCancel();
  };

  const handleClose = () => {
    applySettings();
    onCancel();
  };

  // Handle click outside modal to close
  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      handleClose();
    }
  };

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      lockBodyScroll(); // Lock body scroll when modal opens
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
    } else {
      setIsAnimating(false);
      unlockBodyScroll(); // Unlock body scroll when modal closes
      const timer = setTimeout(() => setShouldRender(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEsc);
      return () => document.removeEventListener("keydown", handleEsc);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!shouldRender) return null;

  return createPortal(
    <div
      className={`song-structure-overlay ${isAnimating ? "open" : ""}`}
      role="dialog"
      aria-modal
      onClick={handleOverlayClick}
    >
      <div
        className={`song-structure-editor ${isAnimating ? "open" : ""}`}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(560px, 92vw)",
          maxHeight: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            padding: "18px 22px",
          }}
        >
          <div style={{ textAlign: "left", marginBottom: "8px" }}>
            <h2
              style={{
                color: "rgba(255, 255, 255, 0.9)",
                fontSize: "22px",
                fontWeight: 600,
                margin: 0,
              }}
            >
              Progressive Tempo
            </h2>
          </div>
        </div>

        <div
          className="modal-content"
          style={{
            padding: "18px 26px",
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            flex: 1,
            overflowY: "auto",
            minHeight: 0,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "12px",
              flexWrap: "wrap",
              fontSize: "18px",
              color: "var(--text-secondary)",
            }}
          >
            <span>Increase BPM by</span>
            <input
              type="number"
              inputMode="numeric"
              maxLength={2}
              value={incrementDisplay}
              onChange={(e) => {
                const val = e.target.value;
                setIncrementDisplay(val);
                // Remove leading zeros and parse
                if (val !== "") {
                  const cleanedVal = val.replace(/^0+/, "") || "0";
                  const parsed = parseInt(cleanedVal, 10);
                  if (!isNaN(parsed)) {
                    setIncrement(parsed);
                  }
                }
              }}
              onFocus={(e) => {
                e.currentTarget.select();
                e.currentTarget.style.borderColor = "rgba(5, 150, 105, 0.5)";
                e.currentTarget.style.background = "rgba(0, 0, 0, 0.2)";
              }}
              onBlur={(e) => {
                handleIncrementValidation();
                e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.1)";
                e.currentTarget.style.background = "rgba(0, 0, 0, 0.15)";
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleIncrementValidation();
                  e.currentTarget.blur();
                }
              }}
              min={INCREMENT_MIN}
              max={INCREMENT_MAX}
              style={{
                width: "80px",
                padding: "12px 16px",
                fontSize: "18px",
                fontWeight: 600,
                textAlign: "center",
                background: "rgba(0, 0, 0, 0.15)",
                color: "rgba(255, 255, 255, 0.95)",
                border: "2px solid rgba(255, 255, 255, 0.1)",
                borderRadius: "12px",
                outline: "none",
                transition: "all 0.2s ease",
              }}
            />
            <span>every</span>
            <input
              type="number"
              inputMode="numeric"
              maxLength={3}
              value={frequencyDisplay}
              onChange={(e) => {
                const val = e.target.value;
                setFrequencyDisplay(val);
                // Remove leading zeros and parse
                if (val !== "") {
                  const cleanedVal = val.replace(/^0+/, "") || "0";
                  const parsed = parseInt(cleanedVal, 10);
                  if (!isNaN(parsed)) {
                    setFrequency(parsed);
                  }
                }
              }}
              onFocus={(e) => {
                e.currentTarget.select();
                e.currentTarget.style.borderColor = "rgba(5, 150, 105, 0.5)";
                e.currentTarget.style.background = "rgba(0, 0, 0, 0.2)";
              }}
              onBlur={(e) => {
                handleFrequencyValidation();
                e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.1)";
                e.currentTarget.style.background = "rgba(0, 0, 0, 0.15)";
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleFrequencyValidation();
                  e.currentTarget.blur();
                }
              }}
              min={FREQUENCY_MIN}
              max={FREQUENCY_MAX}
              style={{
                width: "80px",
                padding: "12px 16px",
                fontSize: "18px",
                fontWeight: 600,
                textAlign: "center",
                background: "rgba(0, 0, 0, 0.15)",
                color: "rgba(255, 255, 255, 0.95)",
                border: "2px solid rgba(255, 255, 255, 0.1)",
                borderRadius: "12px",
                outline: "none",
                transition: "all 0.2s ease",
              }}
            />
            <span>bar(s)</span>
          </div>

          {/* Mobile Apply button - shown only on mobile */}
          <div className="mobile-apply-button" style={{ display: "none" }}>
            <button
              className="save-button"
              onClick={handleApply}
              disabled={!isValid}
              style={{
                padding: "16px 24px",
                fontSize: "16px",
                background: isValid
                  ? "rgba(5, 150, 105, 0.2)"
                  : "rgba(75, 85, 99, 0.3)",
                color: "var(--text-secondary)",
                border: "none",
                borderRadius: "12px",
                cursor: isValid ? "pointer" : "not-allowed",
                fontWeight: "500",
                backdropFilter: "blur(7px)",
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
                transition: "all 0.3s ease",
                position: "relative",
                overflow: "hidden",
                transform: `translateY(0) scale(${saveScale.scale})`,
                width: "100%",
                marginTop: "20px",
              }}
              onMouseEnter={(e) => {
                if (isValid) {
                  e.currentTarget.style.boxShadow =
                    "0 12px 40px rgba(0, 0, 0, 0.2)";
                  e.currentTarget.style.background = "rgba(5, 150, 105, 0.3)";
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow =
                  "0 8px 32px rgba(0, 0, 0, 0.1)";
                e.currentTarget.style.background = isValid
                  ? "rgba(5, 150, 105, 0.2)"
                  : "rgba(75, 85, 99, 0.3)";
              }}
            >
              {/* Pulse Animation Overlay */}
              {savePulse.isActive && (
                <div
                  style={{
                    position: "absolute",
                    left: savePulse.x,
                    top: savePulse.y,
                    width: "0",
                    height: "0",
                    borderRadius: "50%",
                    background: "var(--button-hover-bg)",
                    transform: "translate(-50%, -50%)",
                    animation: "pulseExpand 100ms ease-out forwards",
                    pointerEvents: "none",
                    zIndex: 1,
                  }}
                />
              )}
              Apply
            </button>
          </div>
        </div>

        <div
          className="button-row"
          style={{
            padding: "16px 24px",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <button
            className="save-button"
            onClick={handleApply}
            disabled={!isValid}
            style={{
              padding: "16px 24px",
              fontSize: "16px",
              background: isValid
                ? "rgba(5, 150, 105, 0.2)"
                : "rgba(75, 85, 99, 0.3)",
              color: "var(--text-secondary)",
              border: "none",
              borderRadius: "12px",
              cursor: isValid ? "pointer" : "not-allowed",
              fontWeight: "500",
              backdropFilter: "blur(7px)",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
              transition: "all 0.3s ease",
              position: "relative",
              overflow: "hidden",
              transform: `translateY(0) scale(${saveScale.scale})`,
            }}
            onMouseEnter={(e) => {
              if (isValid) {
                e.currentTarget.style.boxShadow =
                  "0 12px 40px rgba(0, 0, 0, 0.2)";
                e.currentTarget.style.background = "rgba(5, 150, 105, 0.3)";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = "0 8px 32px rgba(0, 0, 0, 0.1)";
              e.currentTarget.style.background = isValid
                ? "rgba(5, 150, 105, 0.2)"
                : "rgba(75, 85, 99, 0.3)";
            }}
          >
            {/* Pulse Animation Overlay */}
            {savePulse.isActive && (
              <div
                style={{
                  position: "absolute",
                  left: savePulse.x,
                  top: savePulse.y,
                  width: "0",
                  height: "0",
                  borderRadius: "50%",
                  background: "var(--button-hover-bg)",
                  transform: "translate(-50%, -50%)",
                  animation: "pulseExpand 100ms ease-out forwards",
                  pointerEvents: "none",
                  zIndex: 1,
                }}
              />
            )}
            Apply
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
